import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ExerciseModal } from "@/components/exercise-modal";
// FoodModal removed - nutrition section removed per user request
import { EditablePlanName } from "@/components/editable-plan-name";
import { UserStatsTooltip } from "@/components/user-stats-tooltip";
import { CoachNotesDisplay } from "@/components/coach-notes-display";
import { Link, useLocation, useParams } from "wouter";
import { Save, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth-context";
import { useMyCoachNotes } from "@/hooks/use-coach";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { GeneratedPlan } from "@/types";
import logger from '@/lib/logger';
import { trackEvent } from '@/lib/analytics/posthog';

export default function PlanPage() {
  const [plan, setPlan] = useState<GeneratedPlan | null>(null);
  const [exerciseModalOpen, setExerciseModalOpen] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState<any>(null);
  const [selectedDayNumber, setSelectedDayNumber] = useState<number | null>(null);
  const [savedPlanData, setSavedPlanData] = useState<any>(null); // For saved plans with metadata
  const { toast } = useToast();
  const { isAuthenticated, isLoading, user } = useAuth();
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const params = useParams();
  const planId = params.id;

  // Query to fetch user's saved plans
  const { data: userPlans } = useQuery({
    queryKey: ['/api/plans/user', user?.id],
    enabled: !!user?.id && !planId, // Only fetch when not viewing a specific plan
  });

  // Query to fetch specific plan by ID
  const { data: specificPlan, isLoading: specificPlanLoading } = useQuery({
    queryKey: ['/api/plans', planId],
    enabled: !!planId,
  });

  // Fetch coach notes for the player
  const { data: coachNotes = [] } = useMyCoachNotes();

  // Mutation to save plan to database
  const savePlanMutation = useMutation({
    mutationFn: async (planData: { userId: number; planData: GeneratedPlan; userProfile: any }) => {
      logger.info("🚀 Sending save request with data:", { userId: planData.userId, hasPlan: !!planData.planData });
      const response = await apiRequest('POST', '/api/plans/save', planData);
      const result = await response.json();
      logger.debug("📥 Save response received:", result);
      return result;
    },
    onSuccess: (data) => {
      logger.info("✅ Save successful:", data);
      try {
        const daysCount = Array.isArray(plan?.days) ? plan!.days.length : 0;
        trackEvent('plan_saved', {
          plan_id: (data as any)?.id,
          days_count: daysCount,
        });
      } catch {}
      toast({
        title: "Plan Saved",
        description: "Your training plan has been saved to your account.",
      });
      // Invalidate and refetch user plans
      queryClient.invalidateQueries({ queryKey: ['/api/plans/user', user?.id] });
    },
    onError: (error: any) => {
      logger.error("❌ Save failed:", error as any);
      toast({
        title: "Save Failed",
        description: error.message || "Failed to save your training plan. Please try again.",
        variant: "destructive",
      });
    }
  });

  useEffect(() => {
    // DON'T clear data automatically - this was causing issues
    // Only clear when explicitly needed
    
    // Redirect to login if not authenticated
    if (!isLoading && !isAuthenticated) {
      setLocation('/login');
      return;
    }

    if (isAuthenticated) {
      if (planId && specificPlan) {
        // Loading a specific plan by ID
        setPlan((specificPlan as any).planData);
        setSavedPlanData(specificPlan);
      } else if (!planId) {
        // Loading current plan - check for ANY generated plan (fresh or existing)
        const storedPlan = localStorage.getItem("generatedPlan");
        const isNewGeneration = localStorage.getItem("isNewGeneration");
        
        logger.debug("Checking for plan in localStorage:");
        logger.debug("- storedPlan exists:", !!storedPlan);
        logger.debug("- storedPlan length:", storedPlan?.length);
        logger.debug("- storedPlan preview:", storedPlan?.substring(0, 100));
        logger.debug("- isNewGeneration:", isNewGeneration);
        
        // Load the plan if it exists, regardless of isNewGeneration flag
        if (storedPlan) {
          try {
            const parsedPlan = JSON.parse(storedPlan);
            logger.debug("Successfully parsed plan:", parsedPlan);
            logger.debug("Plan has days:", !!parsedPlan?.days);
            logger.debug("Number of days:", parsedPlan?.days?.length);
            setPlan(parsedPlan);
            setSavedPlanData(null); // This is not a saved plan
            
            // Only clear the flag if it exists, don't redirect based on it
            if (isNewGeneration === "true") {
              localStorage.removeItem("isNewGeneration");
            }
            } catch (e) {
            logger.error("Failed to parse stored plan:", e as any);
            logger.error("Stored plan that failed to parse:", storedPlan);
            try { trackEvent('plan_parse_error', { error_message: e instanceof Error ? e.message : 'unknown' }); } catch {}
            // Only redirect if the plan is actually corrupted
            setLocation('/form');
          }
        } else {
          // Only redirect if there's truly no plan at all
          logger.info("No plan in localStorage at all, redirecting to form");
          setLocation('/form');
        }
      }
    }
  }, [isAuthenticated, isLoading, setLocation, userPlans, planId, specificPlan]);

  useEffect(() => {
    try {
      if (!plan) return;
      const source = planId ? 'saved' : 'fresh';
      const daysCount = Array.isArray(plan.days) ? plan.days.length : 0;
      trackEvent('plan_viewed', {
        source,
        plan_id: planId || null,
        days_count: daysCount,
      });
    } catch {}
  }, [plan, planId]);

  const handleExerciseClick = (exercise: any, dayNumber: number) => {
    setSelectedExercise(exercise);
    setSelectedDayNumber(dayNumber);
    setExerciseModalOpen(true);
    trackEvent('exercise_clicked', {
      exercise_name: exercise?.exercise || exercise?.name,
      day_number: dayNumber,
      plan_id: planId || null,
    });
  };

  // handleFoodClick removed - nutrition section removed per user request

  const handleSavePlan = () => {
    logger.info("🎯 handleSavePlan called");
    logger.debug("🎯 Current auth state:", { isAuthenticated, isLoading, hasUser: !!user });
    logger.debug("🎯 User details:", user ? { id: user.id, email: user.email } : null);
    
    if (!plan) {
      toast({
        title: "No Plan to Save",
        description: "Please generate a training plan first.",
        variant: "destructive",
      });
      return;
    }

    if (!user?.id) {
      logger.error("❌ No user ID found!", { user, isAuthenticated });
      toast({
        title: "Authentication Required",
        description: "Please sign in to save your training plan.",
        variant: "destructive",
      });
      return;
    }

    logger.info("📝 Attempting to save plan with user:", { id: user.id, email: user.email });
    
    // Get form data for user profile
    const storedFormData = localStorage.getItem("formData");
    let userProfile = null;
    if (storedFormData) {
      try {
        userProfile = JSON.parse(storedFormData);
      } catch (e) {
        logger.warn("Could not parse stored form data for save");
      }
    }

    savePlanMutation.mutate({
      userId: user.id,
      planData: plan,
      userProfile
    });
  };

  const getThemeColor = (day: number) => {
    const colors = [
      "#111111", // was var(--apple-blue)
      "var(--apple-green)",
      "var(--apple-orange)",
      "#111111", // was var(--apple-purple)
      "var(--apple-pink)",
      "#111111", // was var(--apple-blue)
      "var(--apple-green)"
    ];
    return colors[day - 1] || "#111111"; // default neutral
  };

  // Show loading while checking authentication or loading specific plan
  if (isLoading || (planId && specificPlanLoading)) {
    return (
      <div className="min-h-screen py-20 bg-[var(--light-1)] dark:bg-[var(--dark-1)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render anything if not authenticated (redirect is handled in useEffect)
  if (!isAuthenticated) {
    return null;
  }

  if (!plan) {
    return (
      <div className="min-h-screen py-20 bg-[var(--light-1)] dark:bg-[var(--dark-1)] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">No Plan Found</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Please complete the form to generate your personalized training plan.
          </p>
          <Link href="/form">
            <Button className="btn-primary px-8 py-3 rounded-2xl">
              Go to Form
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <section className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:bg-gradient-to-br dark:from-[var(--dark-1)] dark:to-[var(--dark-2)]">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            {savedPlanData ? (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <EditablePlanName
                    planId={savedPlanData.id}
                    currentName={savedPlanData.name || ''}
                    planNumber={1} // We don't have plan number context here, so using 1
                    onNameUpdate={(newName) => {
                      setSavedPlanData({ ...savedPlanData, name: newName });
                    }}
                  />
                </div>
                <div className="text-gray-600 dark:text-gray-300 text-lg flex items-center justify-center gap-3">
                  <span>
                    {plan.days?.filter(day => day.theme !== "Rest Day").length || 0}-day training plan with recovery days designed just for you
                  </span>
                  <a
                    href="https://open.spotify.com/playlist/7BaBYufPTwTMjTXMV2381T?si=WF6-GmO6S_K3QJCZ19Qf2Q"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Your Ultimate Training Playlist"
                    aria-label="Your Ultimate Training Playlist"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-gray-300 dark:border-[var(--dark-3)] hover:bg-gray-100 dark:hover:bg-[var(--dark-3)] transition-all shadow-sm group relative text-[var(--apple-green)]"
                    data-testid="spotify-playlist-link"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      className="w-5 h-5"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="10" fill="currentColor" />
                      <path
                        d="M6.5 9.8c3-.9 7.7-.5 10.3.9"
                        stroke="white"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        fill="none"
                        opacity="0.9"
                      />
                      <path
                        d="M6.7 12.7c2.7-.8 7-.4 9.5.8"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        fill="none"
                        opacity="0.85"
                      />
                      <path
                        d="M7 15.3c2-.6 5.6-.2 7.6.7"
                        stroke="white"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        fill="none"
                        opacity="0.8"
                      />
                    </svg>
                    <span
                      className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs bg-gray-800 text-white dark:bg-[var(--dark-3)] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                    >
                      Your Ultimate Training Playlist
                    </span>
                  </a>
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Your Personalized Training Plan </h2>
                <div className="text-gray-600 dark:text-gray-300 text-lg flex items-center justify-center gap-3">
                  <span>
                    {plan.days?.filter(day => day.theme !== "Rest Day").length || 0}-day training plan with recovery days designed just for you
                  </span>
                  <a
                    href="https://open.spotify.com/playlist/7BaBYufPTwTMjTXMV2381T?si=WF6-GmO6S_K3QJCZ19Qf2Q"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Your Ultimate Training Playlist"
                    aria-label="Your Ultimate Training Playlist"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-gray-300 dark:border-[var(--dark-3)] hover:bg-gray-100 dark:hover:bg-[var(--dark-3)] transition-all shadow-sm group relative text-[var(--apple-green)]"
                    data-testid="spotify-playlist-link"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      className="w-5 h-5"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="10" fill="currentColor" />
                      <path
                        d="M6.5 9.8c3-.9 7.7-.5 10.3.9"
                        stroke="white"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        fill="none"
                        opacity="0.9"
                      />
                      <path
                        d="M6.7 12.7c2.7-.8 7-.4 9.5.8"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        fill="none"
                        opacity="0.85"
                      />
                      <path
                        d="M7 15.3c2-.6 5.6-.2 7.6.7"
                        stroke="white"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        fill="none"
                        opacity="0.8"
                      />
                    </svg>
                    <span
                      className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs bg-gray-800 text-white dark:bg-[var(--dark-3)] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                    >
                      Your Ultimate Training Playlist
                    </span>
                  </a>
                </div>
              </>
            )}
          </div>

          {/* Plan Overview */}
          <div className="mb-12">
            <Card className="glass-card dark:glass-card-dark rounded-3xl p-6">
              <CardContent className="p-0">
                <h3 className="text-xl font-semibold mb-4">Plan Overview</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">{plan.overview || "No overview available"}</p>
                
                {/* User Stats Tooltip - Always show, get data from localStorage if not in saved plan */}
                {(() => {
                  let userProfile = savedPlanData?.userProfile;
                  
                  // If no userProfile in saved plan, try to get from localStorage
                  if (!userProfile) {
                    const storedFormData = localStorage.getItem("formData");
                    logger.debug("Stored form data:", storedFormData);
                    if (storedFormData) {
                      try {
                        userProfile = JSON.parse(storedFormData);
                        logger.debug("Parsed userProfile from localStorage:", userProfile);
                      } catch (e) {
                        logger.warn("Could not parse stored form data", e as any);
                      }
                    }
                  } else {
                    logger.debug("Using userProfile from saved plan:", userProfile);
                  }
                  
                  return userProfile ? (
                    <UserStatsTooltip userProfile={userProfile} />
                  ) : (
                    <div className="mt-6">
                      <div className="inline-flex items-center gap-2 text-gray-400 cursor-not-allowed">
                        <span className="text-sm font-medium">View My Stats</span>
                        <span className="text-xs">(Complete form to see stats)</span>
                      </div>
                    </div>
                  );
                })()}
                {/* Goals and Progress Markers removed per user request */}
              </CardContent>
            </Card>
          </div>

          {/* Coach Notes for this Plan */}
          {planId && coachNotes.length > 0 && (
            <div className="mb-8">
              <CoachNotesDisplay
                notes={coachNotes}
                targetType="plan"
                targetId={parseInt(planId)}
                title="Coach's Notes on This Plan"
              />
            </div>
          )}

          {/* Daily Plans */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {plan.days?.map((day) => (
              <Card
                key={day.day}
                className="glass-card dark:glass-card-dark rounded-3xl shadow-lg card-hover animate-scale-in overflow-hidden relative"
                style={{ animationDelay: `${day.day * 0.1}s` }}
              >
                {/* Card header with gradient */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r" style={{ 
                  background: `linear-gradient(135deg, ${getThemeColor(day.day)}, ${getThemeColor(day.day)}aa)` 
                }}></div>
                
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-2xl flex items-center justify-center shadow-lg" style={{ 
                        background: `linear-gradient(135deg, ${getThemeColor(day.day)}, ${getThemeColor(day.day)}aa)` 
                      }}>
                        <span className="text-white font-bold text-lg">{day.day}</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                          Day {day.day}
                        </h3>
                        <span className="text-sm text-gray-500">{day.training_focus || day.theme}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    {(day.training_focus === "Recovery Day" || day.theme === "Rest Day") ? (
                      /* Rest Day Content */
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-6 border-l-4 border-green-400 shadow-sm text-center">
                        <div className="mb-4">
                          <div className="w-12 h-12 bg-green-400 rounded-full flex items-center justify-center mx-auto mb-3">
                            <span className="text-white text-lg font-bold">😴</span>
                          </div>
                          <h4 className="font-semibold text-green-600 dark:text-green-400 text-lg">
                            Recovery Day
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                            Focus on rest, light stretching, and recovery
                          </p>
                        </div>
                        {/* Recovery day - just show the recovery message */}
                      </div>
                    ) : (
                      /* Training Day Content */
                      <>
                        {/* Warm-up */}
                        {day.warm_up && day.warm_up.length > 0 && (
                          <div className="bg-gradient-to-r from-white to-gray-50 dark:from-[var(--dark-2)] dark:to-[var(--dark-3)] rounded-2xl p-4 border-l-4 border-[var(--apple-green)] shadow-sm">
                            <div className="flex items-center mb-3">
                              <div className="w-6 h-6 bg-[var(--apple-green)] rounded-lg flex items-center justify-center mr-2">
                                <span className="text-white text-xs font-bold">W</span>
                              </div>
                              <h4 className="font-semibold text-[var(--apple-green)]">
                                Warm-up
                              </h4>
                            </div>
                            <div className="space-y-2">
                              {day.warm_up.map((exercise: any, index: number) => (
                                <button
                                  key={index}
                                  onClick={() => handleExerciseClick(exercise, day.day)}
                                  className="text-left hover:text-black dark:hover:text-white transition-all duration-200 text-sm block w-full p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10"
                                  data-testid={`exercise-warmup-${index}`}
                                >
                                  • {exercise.exercise} {exercise.sets && `(${exercise.sets}x${exercise.reps || exercise.duration})`} {exercise.weight && exercise.weight !== 'Bodyweight' && `- ${exercise.weight}`}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Main Training */}
                        {day.main_training && day.main_training.length > 0 && (
                          <div className="bg-gradient-to-r from-white to-gray-50 dark:from-[var(--dark-2)] dark:to-[var(--dark-3)] rounded-2xl p-4 border-l-4 border-[var(--apple-orange)] shadow-sm">
                            <div className="flex items-center mb-3">
                              <div className="w-6 h-6 bg-[var(--apple-orange)] rounded-lg flex items-center justify-center mr-2">
                                <span className="text-white text-xs font-bold">M</span>
                              </div>
                              <h4 className="font-semibold text-[var(--apple-orange)]">
                                Main Training
                              </h4>
                            </div>
                            <div className="space-y-2">
                              {day.main_training.map((exercise: any, index: number) => (
                                <button
                                  key={index}
                                  onClick={() => handleExerciseClick(exercise, day.day)}
                                  className="text-left hover:text-black dark:hover:text-white transition-all duration-200 text-sm block w-full p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10"
                                  data-testid={`exercise-main-${index}`}
                                >
                                  • {exercise.exercise} {exercise.sets && `(${exercise.sets}x${exercise.reps || exercise.duration})`} {exercise.weight && exercise.weight !== 'Bodyweight' && `- ${exercise.weight}`}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Stretching */}
                        {day.stretching && day.stretching.length > 0 && (
                          <div className="bg-gradient-to-r from-white to-gray-50 dark:from-[var(--dark-2)] dark:to-[var(--dark-3)] rounded-2xl p-4 border-l-4 border-gray-400 shadow-sm">
                            <div className="flex items-center mb-3">
                              <div className="w-6 h-6 bg-gray-700 rounded-lg flex items-center justify-center mr-2">
                                <span className="text-white text-xs font-bold">S</span>
                              </div>
                              <h4 className="font-semibold text-gray-800 dark:text-gray-100">
                                Stretching
                              </h4>
                            </div>
                            <div className="space-y-2">
                              {day.stretching.map((exercise: any, index: number) => (
                                <button
                                  key={index}
                                  onClick={() => handleExerciseClick(exercise, day.day)}
                                  className="text-left hover:text-black dark:hover:text-white transition-all duration-200 text-sm block w-full p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10"
                                  data-testid={`exercise-stretching-${index}`}
                                >
                                  • {exercise.exercise} {exercise.sets && `(${exercise.sets}x${exercise.duration || exercise.reps})`} {exercise.weight && exercise.weight !== 'Bodyweight' && `- ${exercise.weight}`}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Plan Actions */}
          <div className="text-center mt-12">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {!planId && (
                <Button
                  onClick={handleSavePlan}
                  disabled={savePlanMutation.isPending}
                  className="btn-primary px-8 py-3 rounded-2xl text-white font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savePlanMutation.isPending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Plan
                    </>
                  )}
                </Button>
              )}

              <Link href="/form">
                <Button
                  variant="outline"
                  className="px-8 py-3 rounded-2xl border-2 border-gray-300 text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-100 dark:hover:bg-[var(--dark-3)] transition-all"
                  onClick={() => trackEvent('plan_regenerated', {
                    current_plan_id: planId || null,
                    is_saved: !!savedPlanData,
                  })}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Generate New Plan
                </Button>
              </Link>

              {planId && (
                <Link href="/my-plans">
                  <Button
                    variant="outline"
                    className="px-8 py-3 rounded-2xl border-2 border-gray-300 text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-100 dark:hover:bg-[var(--dark-3)] transition-all"
                  >
                    Back to My Plans
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ExerciseModal
        isOpen={exerciseModalOpen}
        onClose={() => setExerciseModalOpen(false)}
        exercise={selectedExercise}
        trainingPlanId={savedPlanData?.id}
        dayNumber={selectedDayNumber ?? undefined}
      />
      {/* FoodModal removed - nutrition section removed per user request */}
    </section>
  );
}
